// Строки доски лонча: подпись дорожки или трека (имя правится на месте, действия у правого края, ручка
// порядка) и тело трека с барами, контуром броска и меню «New task here».
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { WriteOnly, useCanWrite } from '@core/auth/access';
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Menu, type MenuItem } from '@core/ui/Menu';
import type { Attention } from '../roadmap/shared/attention';
import { barGeometry, barStyle, trackTop, type Grid } from '../roadmap/shared/geometry';
import { NewTaskHereMenu } from '../roadmap/shared/NewTaskHere';
import { NewTaskGhost, type QuickHandlers } from '../roadmap/shared/QuickTask';
import { GhostBar, type DragKind } from '../roadmap/shared/TaskBar';
import { ReorderHandle, RowActions, rowActionButton, rowAddButton } from '../roadmap/shared/RowActions';
import type { TaskDrag } from '../roadmap/shared/interaction';
import { LaunchBar } from './LaunchBar';
import { LaunchLines } from './LaunchHeader';
import { hintAt, statusOf, type Hint, type LaunchRow, type LaunchTask, type Preview } from './planner';

export type Drag = TaskDrag<null, Preview, LaunchTask>;

type LabelProps = {
  row: LaunchRow; onRename: (name: string) => void; onAdd: () => void; onDelete: () => void; onHandle: (e: PointerEvent<HTMLElement>) => void;
  onColor?: (color: string) => void;   // дорожка: цвет подписи и кромки, правится кружком
};

/** Подпись строки: клик по карандашу — имя в поле, Enter/blur сохраняет, Escape отменяет. */
export function LaunchRowLabel({ row, onRename, onAdd, onDelete, onHandle, onColor }: LabelProps) {
  const [editing, setEditing] = useState<string | null>(null);
  const isLane = row.kind === 'swimlane';
  // цвет под курсором, пока пикер открыт: React onChange у type=color — это input при каждом движении, а запись
  // одна, по нативному change (пикер закрыт); иначе десятки записей подряд упирались в expect_updated_at и предохранитель
  const [picking, setPicking] = useState<string | null>(null);
  const colorInput = useRef<HTMLInputElement>(null);
  const latestColor = useRef(onColor);
  latestColor.current = onColor;
  useEffect(() => {
    const el = colorInput.current;
    if (!el) return;
    const commit = (e: Event) => { setPicking(null); latestColor.current?.((e.target as HTMLInputElement).value); };
    el.addEventListener('change', commit);
    return () => el.removeEventListener('change', commit);
  }, []);
  const color = row.kind === 'swimlane' ? (picking ?? row.color) : null;
  const commit = () => { if (editing !== null && editing.trim() && editing.trim() !== row.label) onRename(editing.trim()); setEditing(null); };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } };
  const canWrite = useCanWrite();
  const menu: MenuItem[] = canWrite ? [
    { label: isLane ? 'New track' : 'New task', onSelect: onAdd, icon: Plus },
    { label: 'Rename', onSelect: () => setEditing(row.label), icon: Pencil },
    'sep',
    { label: isLane ? 'Delete swimlane' : 'Delete track', onSelect: onDelete, danger: true, icon: Trash2 },
  ] : [];
  return (
    <Menu items={menu}>
      <div className={`group relative h-full flex items-center gap-1 py-1 ${isLane ? 'px-1 font-bold' : 'pl-4 pr-2 text-sm text-gray-600'}`} style={color ? { color } : undefined} data-launch-swimlane={isLane ? row.id : undefined} data-launch-track={isLane ? undefined : row.id}>
        <WriteOnly><ReorderHandle label={row.label} onPointerDown={onHandle} /></WriteOnly>
        {color && onColor && canWrite && (
          <label className="relative inline-flex shrink-0 cursor-pointer" title="Swimlane colour">
            <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} aria-hidden />
            <input ref={colorInput} type="color" value={color} aria-label={`Colour of ${row.label}`} className="absolute inset-0 w-3 h-3 opacity-0 cursor-pointer" onChange={(e) => setPicking(e.target.value)} />
          </label>
        )}
        {editing !== null ? (
          <input autoFocus className="min-h-0 border border-line rounded px-1 py-0.5 text-sm w-full bg-white font-normal" value={editing} aria-label={`Rename ${row.label}`}
            onChange={(e) => setEditing(e.target.value)} onBlur={commit} onKeyDown={onKey} />
        ) : (
          <span className="truncate" title={row.label} data-launch-row-name onDoubleClick={() => canWrite && setEditing(row.label)}>{row.label}</span>
        )}
        <WriteOnly>
          <RowActions bg={isLane ? 'bg-gray-100' : 'bg-white'}>
            <button type="button" className={`${rowActionButton} text-ink-muted`} onClick={() => setEditing(row.label)} aria-label={`Rename ${row.label}`} title="Rename">✎</button>
            <button type="button" className={`${rowActionButton} text-ink-muted`} onClick={onDelete} aria-label={`Delete ${row.label}`} title={isLane ? 'Delete swimlane with its tracks and tasks' : 'Delete track with its tasks'}>×</button>
            <button type="button" className={rowAddButton} onClick={onAdd} aria-label={isLane ? `Add track to ${row.label}` : `Add task to ${row.label}`} title={isLane ? 'Add track' : 'Add task'}>+</button>
          </RowActions>
        </WriteOnly>
      </div>
    </Menu>
  );
}

type GridProps = {
  row: Extract<LaunchRow, { kind: 'track' }>; grid: Grid; todayX: number | null; barH: number; wrap: boolean;
  blockers: ReadonlyMap<string, LaunchTask[]>;   // невыполненные блокеры по задаче — один расчёт на доску
  attention: Map<string, Attention>; pendingIds: Set<string>; selected: ReadonlySet<string>; onHover: (id: string | null) => void;
  drag: Drag | null; groupShift: { ids: Set<string>; days: number } | null; hint: Hint | null; onHint: (h: Hint | null) => void;
  onBarPointerDown: (t: LaunchTask, kind: DragKind, e: PointerEvent<HTMLElement>) => void;
  onOpen: (t: LaunchTask) => void; onKey: (t: LaunchTask, e: KeyboardEvent<HTMLElement>) => void; menuFor: (t: LaunchTask) => MenuItem[];
  onNewAt: (date: string) => void;
  quick: LaunchQuick | null; onQuickAt: (h: Hint) => void;
};
/** Быстрая задача лонча на месте контура: только название (исполнителей у задач лонча нет). */
export type LaunchQuick = QuickHandlers<Hint>;

export function LaunchRowGrid({ row, grid, todayX, barH, wrap, blockers, attention, pendingIds, selected, onHover, drag, groupShift, hint, onHint, onBarPointerDown, onOpen, onKey, menuFor, onNewAt, quick, onQuickAt }: GridProps) {
  const subRows = row.subRows;
  return (
    <>
      <LaunchLines grid={grid} todayX={todayX} />
      <NewTaskHereMenu grid={grid} hintOf={(date) => hintAt(row.id, date)} onHint={onHint} onNewAt={onNewAt} onQuickAt={onQuickAt} />
      <NewTaskGhost hint={hint} quick={quick} grid={grid} place={() => ({ top: trackTop(subRows, barH), height: barH })} form={{ assignees: false, duration: 'three-day' }} />
      {row.tasks.map((t) => {
        const box = barGeometry(t, grid);
        if (!box) return null;
        const isDragged = drag?.task.id === t.id;
        const shifted = !isDragged && !!groupShift?.ids.has(t.id);
        const style = barStyle(box, { dragged: isDragged, kind: drag?.kind, dx: drag?.dx, previewBox: drag?.preview?.kind === 'resize' ? barGeometry(drag.preview, grid) : null, shiftDays: shifted ? groupShift!.days : null }, grid.dayWidth);
        const shown = statusOf(t, blockers);
        return (
          <LaunchBar key={t.id} task={t} box={{ ...box, ...style }} top={trackTop(row.subRow.get(t.id) ?? 0, barH)} height={barH} shown={shown} dayWidth={grid.dayWidth}
            blockedBy={shown === 'blocked' ? (blockers.get(t.id) ?? []).map((b) => b.title) : []}
            attention={attention.get(t.id)} pending={pendingIds.has(t.id)} ghost={(isDragged && drag?.kind === 'move') || shifted} selected={selected.has(t.id)} onHover={onHover} wrap={wrap}
            onPointerDown={(kind, e) => onBarPointerDown(t, kind, e)} onOpen={() => onOpen(t)} onKey={(e) => onKey(t, e)} menu={menuFor(t)} />
        );
      })}
      {drag?.preview?.kind === 'move' && drag.preview.track_id === row.id && (() => {
        const g = barGeometry(drag.preview, grid);
        return g ? <GhostBar left={g.left} width={g.width} top={trackTop(subRows, barH)} height={barH}>{drag.task.title}</GhostBar> : null;
      })()}
    </>
  );
}

